import React, { useState, useCallback, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/migration/chevron-up';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { INCOMING, OUTGOING } from '../../../constants/dependency.tsx';
import type { Appearance, OnUnlinkCallback, OverlayItem } from '../../../types/dependency.tsx';
import { isDependencyDatesOverlapping as isOverlapping } from '../../../utils/dependency/overlap.tsx';
import { createUnlinkHandlerByAppearance } from '../../../utils/dependency/unlink-button.tsx';
import Card from '../card/index.tsx';
import Separator from '../separator/index.tsx';

export type Props = {
	item: OverlayItem;
	dependencyItems: OverlayItem[];
	description: string;
	appearance: Appearance;
	onUnlink?: OnUnlinkCallback; // optional handler, controls visibility of the unlink button,
	onClose: () => void;
	isExpanded?: boolean;
};

const ExpandableCard = ({
	dependencyItems,
	item,
	description,
	appearance,
	onUnlink,
	onClose,
	isExpanded = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(isExpanded);

	const Chevron = isOpen ? ChevronUpIcon : ChevronDownIcon;

	const hasFilteredItems = dependencyItems.some(
		(dependencyItem: OverlayItem) => dependencyItem.isHidden,
	);

	const onToggleExpand = useCallback(
		(event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			const toggleOn = !isOpen;
			fireUIAnalytics(analyticsEvent, 'expandDependencySummaryButton', { toggle: toggleOn });
			setIsOpen(toggleOn);
		},
		[isOpen],
	);

	const isItemDatesOverlap = (startDate?: number, dueDate?: number): boolean =>
		appearance === INCOMING
			? isOverlapping(startDate, dueDate, item.startDate, item.dueDate)
			: isOverlapping(item.startDate, item.dueDate, startDate, dueDate);

	// Check if any of the dependency items has overlapped dates
	const hasOverlapDate = dependencyItems.some((dependencyItem: OverlayItem) =>
		isItemDatesOverlap(dependencyItem.startDate, dependencyItem.dueDate),
	);

	return (
		<DependencyDetailContainer>
			<Card appearance={appearance} {...item} />
			<ExpandButton>
				<IconButton
					icon={Chevron}
					spacing="compact"
					appearance="subtle"
					testId="roadmap.timeline-table-kit.common.ui.dependency.expandable-card.button"
					onClick={onToggleExpand}
					label={`${item.itemKey} ${item.title}`}
					aria-expanded={isOpen}
					{...(fg('platform_button_item-add-ufo-metrics')
						? { interactionName: 'timeline-dependency-expandable-card-expand-clicked' }
						: {})}
				/>
			</ExpandButton>
			{isOpen && (
				<>
					<Separator
						label={description}
						unlinkLabel={
							dependencyItems.length === 1
								? `${item.itemKey} ${item.title} ${description} ${dependencyItems[0].itemKey} ${dependencyItems[0].title}`
								: undefined
						}
						isOverlapping={hasOverlapDate}
						isFiltered={hasFilteredItems}
						{...(onUnlink && dependencyItems.length === 1
							? {
									onUnlink: createUnlinkHandlerByAppearance(
										item.id,
										dependencyItems[0].id,
										appearance,
										onUnlink,
										dependencyItems.length ? undefined : onClose,
									),
								}
							: undefined)}
					/>
					{dependencyItems.map((dependencyItem: OverlayItem) => (
						<Card
							key={dependencyItem.id}
							unlinkLabel={`${item.itemKey} ${item.title} ${description} ${dependencyItem.itemKey} ${dependencyItem.title}`}
							appearance={appearance === INCOMING ? OUTGOING : INCOMING}
							{...dependencyItem}
							isOverlapping={isItemDatesOverlap(dependencyItem.startDate, dependencyItem.dueDate)}
							{...(onUnlink && dependencyItems.length > 1
								? {
										onUnlink: createUnlinkHandlerByAppearance(
											item.id,
											dependencyItem.id,
											appearance,
											onUnlink,
										),
									}
								: undefined)}
						/>
					))}
				</>
			)}
		</DependencyDetailContainer>
	);
};

export default ExpandableCard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DependencyDetailContainer = styled.div({
	position: 'relative',
	marginTop: token('space.100', '8px'),
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandButton = styled.div({
	display: 'block',
	position: 'absolute',
	top: 0,
	right: token('space.negative.400', '-32px'),
	width: '24px',
});
